import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef, useCallback, } from 'react';

import { Map, Iterable } from 'immutable';
import WaveSurfer        from 'wavesurfer.js';
import isEmpty           from 'lodash/isEmpty';

import Grid          from '@mui/material/Grid';
import Box           from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import grey          from '@mui/material/colors/grey';
import Typography    from '@mui/material/Typography';
import { useTheme }  from '@mui/material/styles';
import PlayCircle    from '@mui/icons-material/PlayCircle';

import { Display }   from 'components';
import { checkLocalStorage } from 'helpers/local_storage';

const VIDEO_CONTENT_TYPES = ['video/quicktime', 'video/mp4'];
const AUDIO_CONTENT_TYPES = ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/mp4'];

function PlayMedia({ name = null, value = null, onChange = null, mediaUrl, contentType, replayable, id, disabled }) {
  if (!checkLocalStorage()) return <Typography data-testid="play-media-no-localstorage">{t('eos.landing_page.text.play_media.local_storage_not_present')}</Typography>

  const onPlay = useCallback(() => {
    onChange(name, new Map({ text: t('eos.landing_page.text.play_media.answer') }));
  }, [onChange]);

  useEffect(() => {
    // Disable right click
    const handle = (e) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handle);

    return () => document.removeEventListener("contextmenu", handle);
  }, []);

  if (VIDEO_CONTENT_TYPES.includes(contentType)) {
    return <Video onChange={onPlay} mediaUrl={mediaUrl} replayable={replayable} id={id} disabled={disabled} />
  }

  if (AUDIO_CONTENT_TYPES.includes(contentType)) {
    return <Audio onChange={onPlay} mediaUrl={mediaUrl} replayable={replayable} id={id} disabled={disabled} />
  }

  return (
    <Typography data-testid="play-media-unknown-format">{t('eos.landing_page.text.play_media.local_storage_not_present')}</Typography>
  );
}

function Audio({ onChange, mediaUrl, replayable, id, disabled }) {
  const [state, setState] = useState(new Map());
  const theme = useTheme();

  const fileLoaded = state.get('fileLoaded', false);
  const played = state.get('played', localStorage.getItem(id));

  const waveSurferObjectRef = useRef();
  const refWavesurfer       = useRef();

  useEffect(() => {
    if (isEmpty(mediaUrl) || isEmpty(refWavesurfer.current)) return;

    if (isEmpty(waveSurferObjectRef.current)) {
      waveSurferObjectRef.current = WaveSurfer.create({
        container:     refWavesurfer.current,
        waveColor:     theme.palette.primary.main,
        progressColor: theme.palette.primary.dark,
        barHeight:     1,
        barWidth:      1,
        normalize:     true,
        backend:       'WebAudio',
        interact:      false,
      });
    }

    waveSurferObjectRef.current.load(mediaUrl);
    waveSurferObjectRef.current.on('ready', () => setState(state.set('fileLoaded', true)));

    return () => waveSurferObjectRef.current && waveSurferObjectRef.current.destroy();
  }, [mediaUrl, refWavesurfer]);

  useEffect(() => {
    if (!played) return;

    setTimeout(() => onChange(), 100);
  }, [played]);

  const play = () => {
    if (isEmpty(waveSurferObjectRef.current)) return;

    waveSurferObjectRef.current.play(0);
    setState(state.set('played', true));

    if (!replayable) localStorage.setItem(id, true);
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 100, justifyContent: 'center', alignItems: 'center', p: 2, }}>
          <Box ref={refWavesurfer} data-testid="play-media-audio-wavesurfer" sx={{ width: '90%', borderColor: grey[500] }} />
          <LoadingButton sx={{ mt: 1 }} loading={!fileLoaded} disabled={disabled || (played && !replayable)} data-testid="play-media-audio-play" variant="outlined" color={'primary'} onClick={() => play()} startIcon={<PlayCircle />}>{t('eos.landing_page.label.play')}</LoadingButton>
        </Box>
      </Grid>
    </Grid>
  );
}

function Video({ onChange, mediaUrl, replayable, id, disabled }) {
  const [state, setState] = useState(new Map());

  const fileLoaded = state.get('fileLoaded', false);
  const played = state.get('played', localStorage.getItem(id));
  const current = state.get('current', 0);

  const videoPreviewRef = useRef();

  useEffect(() => {
    if (isEmpty(mediaUrl) || isEmpty(videoPreviewRef.current)) return;

    videoPreviewRef.current.oncanplaythrough = () => {
      setState(state.set('fileLoaded', true).set('current', videoPreviewRef.current.duration));
    };

    videoPreviewRef.current.onplay = () => {
      setState(state.set('played', true));
    };

    videoPreviewRef.current.src = mediaUrl;
    videoPreviewRef.current.load(); // Important for iOS
  }, [mediaUrl, videoPreviewRef.current]);

  useEffect(() => {
    if (!played) return;

    setTimeout(() => onChange(), 100);

    const interval = setInterval(() => {
      setState(state.set('current', videoPreviewRef.current.duration - videoPreviewRef.current.currentTime));
    }, 1000);

    return () => {
      if (interval) clearInterval(interval);
    }
  }, [played]);

  const play = () => {
    videoPreviewRef.current.play();

    setState(state.set('played', true));
    if (!replayable) localStorage.setItem(id, true);
  };

  const formatTime = (time) => {
    const minutes = parseInt(time / 60, 10);
    let seconds   = parseInt(time % 60, 10);

    seconds = seconds < 10 ? `0${seconds}` : seconds;

    return `${minutes}:${seconds}`;
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2, }}>
          <video
            ref={videoPreviewRef}
            style={{ width: '100%', maxHeight: '50vh' }}
            playsInline
            // height="240"
            data-testid="play-media-video-player"
            preload="auto"
            controls={replayable}
          />

          <Display if={!replayable}>
            <Typography>{formatTime(current)}</Typography>
          </Display>

          <LoadingButton sx={{ mt: 2 }} loading={!fileLoaded} disabled={disabled || (played && !replayable)} data-testid="play-media-video-play" variant="outlined" color={'primary'} onClick={() => play()} startIcon={<PlayCircle />}>{t('eos.landing_page.label.play')}</LoadingButton>
        </Box>
      </Grid>
    </Grid>
  );
}

PlayMedia.propTypes = {
  name:     PropTypes.string,
  value:    PropTypes.objectOf(Iterable),
  onChange: PropTypes.func,
};

export default PlayMedia;
